import { useEffect, useState } from "react";
import { Phone, Check, RotateCcw, Trash2, Clock, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function when(iso) {
  try {
    return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

export default function AdminCallbacks() {
  const [list, setList] = useState([]);

  const load = async () => {
    const { data } = await api.get("/callbacks/all");
    setList(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async (cb) => {
    await api.patch(`/callbacks/${cb.id}`, { handled: !cb.handled });
    await load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete this callback request?")) return;
    await api.delete(`/callbacks/${id}`);
    toast.success("Deleted");
    await load();
  };

  const pending = list.filter((c) => !c.handled);
  const handled = list.filter((c) => c.handled);

  return (
    <div data-testid="admin-callbacks">
      <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#ff6b00]">Leads</div>
      <h1 className="font-display mt-2 text-3xl sm:text-4xl font-extrabold uppercase text-slate-100">Callback requests</h1>

      {list.length === 0 && <div className="cj-card mt-6 p-6 text-slate-400 text-sm" data-testid="callbacks-empty">No callback requests yet. When a customer leaves their number on the site, it shows up here.</div>}

      {pending.length > 0 && (
        <>
          <div className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">To call back ({pending.length})</div>
          <div className="mt-3 grid gap-3">
            {pending.map((c) => <Row key={c.id} c={c} onToggle={() => toggle(c)} onDelete={() => del(c.id)} />)}
          </div>
        </>
      )}

      {handled.length > 0 && (
        <>
          <div className="mt-8 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">Done ({handled.length})</div>
          <div className="mt-3 grid gap-3 opacity-60">
            {handled.map((c) => <Row key={c.id} c={c} onToggle={() => toggle(c)} onDelete={() => del(c.id)} />)}
          </div>
        </>
      )}
    </div>
  );
}

function Row({ c, onToggle, onDelete }) {
  return (
    <div className="cj-card p-5 flex flex-wrap items-start justify-between gap-4" data-testid={`callback-row-${c.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-display uppercase font-extrabold text-lg text-slate-100">{c.name}</span>
          <a href={`tel:${c.phone}`} data-testid={`callback-call-${c.id}`} className="inline-flex items-center gap-1 font-display font-bold text-[#ff6b00] hover:opacity-80">
            <Phone className="h-4 w-4" /> {c.phone}
          </a>
        </div>
        <div className="font-mono text-xs text-slate-500 mt-2 flex flex-wrap gap-x-5 gap-y-1">
          <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> Best time: {c.preferred_time || "Anytime"}</span>
          <span>Received {when(c.created_at)}</span>
        </div>
        {c.message && <div className="mt-2 text-sm text-slate-300 inline-flex items-start gap-2"><StickyNote className="h-3.5 w-3.5 text-slate-500 mt-0.5" /> {c.message}</div>}
      </div>
      <div className="flex gap-2">
        <button onClick={onToggle} data-testid={`callback-toggle-${c.id}`} className="cj-btn-ghost px-3 py-2 rounded-lg text-xs inline-flex items-center gap-1">
          {c.handled ? <><RotateCcw className="h-3.5 w-3.5" /> Reopen</> : <><Check className="h-3.5 w-3.5" /> Mark called</>}
        </button>
        <button onClick={onDelete} data-testid={`callback-delete-${c.id}`} className="px-3 py-2 rounded-lg text-xs text-red-400 border border-red-500/30 hover:bg-red-500/10">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
